import { get as getItemSessionStorage } from './sessionStorage'
import { addSessionTokenHeaders, handleSessionTokenError } from './apiClient'

const { fetch: originalFetch } = window

const setup = ({ ok = {}, ko = {} }) => {
  window.fetch = async (...args) => {
    const [resource, config] = args
    const response = await originalFetch(resource, config)
    if (!response.ok && typeof ko[response.status] === 'function') {
      ko[response.status](response)
      return Promise.reject(response)
    }
    if (response.ok && typeof ok[response.status] === 'function') {
      ok[response.status](response)
      return Promise.resolve(response)
    }
    return response
  }
}

const urlBase = process.env.REACT_APP_BACKEND_URL
const backEntryPoint = process.env.REACT_APP_BACKEND_ENTRY_POINT

const makeCommonHeaders = async (extraHeaders = {}, withAuthorizationHeader = false) => {
  let authorizationHeader = {}

  if (withAuthorizationHeader) {
    const accessToken = getItemSessionStorage('accessToken')
    const idToken = getItemSessionStorage('idToken')
    authorizationHeader = accessToken && idToken ? { Authorization: `Bearer ${accessToken}`, 'X-Id-Token': idToken } : {}
  }

  // Agregar headers de session token
  const sessionHeaders = await addSessionTokenHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...authorizationHeader,
    ...extraHeaders,
  })

  return sessionHeaders
}

const makeBody = (data) => (data != null ? JSON.stringify(data) : undefined)
const readUrl = (url = '', entryPoint = backEntryPoint, urlPrefix = urlBase) => new URL(`${urlPrefix}/${entryPoint}/${url}`).href

const checkResponse = async (response) => {
  if (!response.ok) {
    throw response
  }

  if (response.status === 204) {
    // 204 status does not have data
    return response
  }

  if (response.status === 401) {
    // 401 status does not have data
    window.location.href = '/logout'
  }

  const data = await response.json()
  return data
}

const checkBlob = async (response) => {
  if (!response.ok) {
    throw response
  }

  if (response.status === 204) {
    return response
  }

  try {
    if (response.headers.get('content-type').includes('spreadsheetml')) {
      const data = await response.blob()
      const fileName = 'Import-users-failed.xlsx'
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      createFile(data, fileName, fileType)
      response.blob = true
      return response
    } else {
      response.blob = false
      return response
    }
  } catch {
    throw response
  }
}

const get = async (url = '', withAuthorizationHeader = true, headers = {}, entryPoint = backEntryPoint, urlPrefix = urlBase) => {
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  const res = await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'GET',
    headers: requestHeaders,
  })

  // Manejar errores de session token
  if (!res.ok) {
    await handleSessionTokenError(res)
  }

  return await checkResponse(res)
}

const getError = async (
  url = '',
  withAuthorizationHeader = true,
  headers = {},
  entryPoint = backEntryPoint,
  urlPrefix = urlBase
) => {
  /* if (withAuthorizationHeader) {
    const accessToken = getItemSessionStorage('accessToken')
    const idToken = getItemSessionStorage('idToken')

    if (!accessToken || !idToken) {
      throw new Error('No se puede realizar la petición: Tokens de autenticación no disponibles.')
    }
  }*/
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  return await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'GET',
    headers: requestHeaders,
  })
    .then((response) => response.json())
    .then((data) => {
      return data
    })
}

const post = async (
  url,
  data,
  withAuthorizationHeader = true,
  headers = {},
  entryPoint = backEntryPoint,
  urlPrefix = urlBase
) => {
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  const res = await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'POST',
    headers: requestHeaders,
    body: makeBody(data),
  })

  // Manejar errores de session token
  if (!res.ok) {
    await handleSessionTokenError(res)
  }

  return checkResponse(res)
}
const postError = async (
  url,
  data,
  withAuthorizationHeader = true,
  headers = {},
  entryPoint = backEntryPoint,
  urlPrefix = urlBase
) => {
  /* if (withAuthorizationHeader) {
    const accessToken = getItemSessionStorage('accessToken')
    const idToken = getItemSessionStorage('idToken')

    if (!accessToken || !idToken) {
      throw new Error('No se puede realizar la petición: Tokens de autenticación no disponibles.')
    }
  }*/
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  return await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'POST',
    headers: requestHeaders,
    body: makeBody(data),
  })
    .then((response) => response.json())
    .then((data) => {
      return data
    })
}

const putError = async (
  url,
  data,
  withAuthorizationHeader = true,
  headers = {},
  entryPoint = backEntryPoint,
  urlPrefix = urlBase
) => {
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  const response = await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'PUT',
    headers: requestHeaders,
    body: makeBody(data),
  })

  // Manejar errores de session token
  await handleSessionTokenError(response)

  const responseData = await response.json()

  if (!response.ok) {
    const error = new Error(responseData?.error || responseData?.message || 'Error en la petición')
    error.response = responseData
    error.status = response.status
    throw error
  }

  return responseData
}

const patchError = async (
  url,
  data,
  withAuthorizationHeader = true,
  headers = {},
  entryPoint = backEntryPoint,
  urlPrefix = urlBase
) => {
  const requestHeaders = await makeCommonHeaders(
    {
      ...headers,
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
    },
    withAuthorizationHeader
  )
  const response = await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'PATCH',
    headers: requestHeaders,
    body: makeBody(data),
  })

  // Manejar errores de session token
  await handleSessionTokenError(response)

  const responseData = await response.json()

  if (!response.ok) {
    const error = new Error(responseData?.error || responseData?.message || 'Error en la petición')
    error.response = responseData
    error.status = response.status
    throw error
  }

  return responseData
}

const postMultipart = async (
  url,
  formData,
  withAuthorizationHeader = true,
  headers = {},
  entryPoint = backEntryPoint,
  checkFunction,
  urlPrefix = urlBase
) => {
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  delete requestHeaders['Content-Type']
  if (url?.includes('blacklist_cups')) {
    const res = await fetch(readUrl(url, entryPoint), {
      method: 'POST',
      headers: requestHeaders,
      body: formData,
    }).then((response) => response.json())
    return res
  } else {
    const res = await fetch(readUrl(url, entryPoint, urlPrefix), {
      method: 'POST',
      headers: requestHeaders,
      body: formData,
    })
    return checkFunction ? checkFunction(res) : checkResponse(res)
  }
}

const put = async (url, data, withAuthorizationHeader = true, headers = {}, entryPoint = backEntryPoint, urlPrefix = urlBase) => {
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  const res = await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'PUT',
    headers: requestHeaders,
    body: makeBody(data),
  })

  // Manejar errores de session token
  if (!res.ok) {
    await handleSessionTokenError(res)
  }

  return checkResponse(res)
}

const patch = async (
  url,
  data,
  withAuthorizationHeader = true,
  headers = {},
  entryPoint = backEntryPoint,
  urlPrefix = urlBase
) => {
  const requestHeaders = await makeCommonHeaders(
    {
      ...headers,
      Accept: 'application/ld+json',
      'Content-Type': 'application/merge-patch+json',
    },
    withAuthorizationHeader
  )
  const res = await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'PATCH',
    headers: requestHeaders,
    body: makeBody(data),
  })

  // Manejar errores de session token
  if (!res.ok) {
    await handleSessionTokenError(res)
  }

  return checkResponse(res)
}

const del = async (url, withAuthorizationHeader = true, headers = {}, entryPoint = backEntryPoint, urlPrefix = urlBase) => {
  const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
  const res = await fetch(readUrl(url, entryPoint, urlPrefix), {
    method: 'DELETE',
    headers: requestHeaders,
  })

  // Manejar errores de session token
  if (!res.ok) {
    await handleSessionTokenError(res)
  }

  return checkResponse(res)
}

// const del = (url, data, headers = {}) => {
//   const res = yield call(fetch, readUrl(url), {
//     method: 'DELETE',
//     headers: yield call(makeCommonHeaders, headers),
//     body: makeBody(data),
//   })
//   return checkResponse(res)
// }

const createFile = (blob, filename, filetype) => {
  const path = window.URL.createObjectURL(
    new Blob([blob], {
      type: filetype,
    })
  )
  const link = document.createElement('a')
  link.href = path
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.parentNode.removeChild(link)
}

// method to download a file from endpoint
const getFile = async (
  url = '',
  withAuthorizationHeader = false,
  headers = {},
  entryPoint = backEntryPoint,
  urlPrefix = urlBase
) => {
  try {
    const requestHeaders = await makeCommonHeaders(headers, withAuthorizationHeader)
    const res = await fetch(readUrl(url, entryPoint, urlPrefix), {
      method: 'GET',
      headers: requestHeaders,
      responseType: 'blob',
    })
    if (res.ok) {
      let blob = await res.blob()
      const contentDisposition = decodeURIComponent(res.headers.get('content-disposition'))
      const filename = contentDisposition?.split("''")[1] || contentDisposition?.split('filename=')[1]
      const fileType = res.headers.get('content-type')
      createFile(blob, filename, fileType)
    } else {
      throw res
    }
  } catch (error) {
    console.error(error)
  }
}

export { get, getFile, post, postMultipart, put, patch, del, checkBlob, setup, getError, postError, putError, patchError }
